import { WorkPage } from '../components/work-page.tsx';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';
import { PlanRequiredNotice, usePlanFeature } from '../components/plan-gate.tsx';
import { classes } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import type { PhotoIntakeSummary } from '@shared/types/photo-quotes.ts';

/** Saved queue URLs retain their gates and open the photo view of Requests. */
export default function PhotoQuotesPage() {
  const { t } = useTranslation();
  const { company } = useSession();
  const hasQueue = usePlanFeature('magic_quote');
  useDocumentTitle(t('photoQuotes.queue.documentTitle'));

  // The deployment flag hides the feature outright; a saved link goes back to Quotes.
  if (company && !company.photoQuotesEnabled) return <Navigate to="/quotes" replace />;
  if (!hasQueue) {
    return (
      <WorkPage title={t('photoQuotes.queue.title')}>
        <PlanRequiredNotice feature="magic_quote">
          <p className="mt-2">
            <Link to="/quotes/photos/demo" className="font-semibold underline">
              {t('photoQuotes.demo.tryLink')}
            </Link>
          </p>
        </PlanRequiredNotice>
      </WorkPage>
    );
  }
  return <Navigate to="/requests?source=photo_dump&photoFilter=open" replace />;
}

const STATUS_TONE: Record<string, string> = {
  analyzing: 'text-[color:var(--color-muted)]',
  awaiting_owner: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  needs_more_photos: 'border-[color:var(--color-warning)]',
  sent: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  closed: 'text-[color:var(--color-muted)]',
  expired: 'text-[color:var(--color-muted)]',
};

export function PhotoIntakeStatusBadge({ status }: { status: PhotoIntakeSummary['status'] }) {
  const { t } = useTranslation();
  return <span className={classes('badge', STATUS_TONE[status])}>{t(`photoQuotes.status.${status}`)}</span>;
}
